import { createFileRoute } from "@tanstack/react-router"
import { useDefaultInfiniteQuery } from "@/utils/useDefaultInfiniteQuery"
import {
  getPaginatedJobLogs,
  JobLogDto,
  PaginatedJobLogsRequest,
  useGetAllSuspense,
} from "@/orval/gen"
import { Page } from "@components/Page"
import {
  ResponsiveFilterGroup,
  useResponsiveFilterQueryOptions,
} from "../../../-components/hideable-filter-group"
import { DateTimePicker } from "@mantine/dates"
import { useUserLocalStorage } from "../../../-components/use-user-local-storage"
import { MultiSelect } from "@mantine/core"
import {
  InfiniteScrollingTable,
  TheasoftColumnDef,
} from "@/routes/-components/custom-tanstack-table"
import { VisibilityState } from "@tanstack/react-table"
import { getFormattedDate } from "@/utils/get-formatted-date"

function renderJobNameAndType(name: string, jobType: string) {
  return name === jobType ? name : `${name} (${jobType})`
}

export const Route = createFileRoute(
  "/app/admin-settings/automatic-background-jobs/logs/"
)({
  component: Component,
  beforeLoad: () => ({ getTitle: () => "Logs" }),
  validateSearch: (
    search: Omit<PaginatedJobLogsRequest, "pageParam" | "pageSize">
  ) => search,
})

function Component() {
  const search = Route.useSearch()
  const [filterVisible, setFilterVisible] = useUserLocalStorage({
    key: "automatic-background-jobs-table-filter-visible",
    defaultValue: true,
  })
  const queryOptions = useResponsiveFilterQueryOptions(filterVisible)
  const queryResult = useDefaultInfiniteQuery(
    getPaginatedJobLogs,
    (pageRequest) => ({
      ...search,
      start: search.start ? new Date(search.start).toISOString() : undefined,
      end: search.end ? new Date(search.end).toISOString() : undefined,
      ...pageRequest,
    }),
    queryOptions
  )
  const navigate = Route.useNavigate()
  const updateSearch = (input: Partial<typeof search>) => {
    navigate({ search: { ...search, ...input } })
  }
  const { data: jobs } = useGetAllSuspense()
  const jobFilterOptions = React.useMemo(() => {
    return jobs.map((job) => ({
      value: job.jobId!.toString(),
      label: renderJobNameAndType(job.name, job.jobType!),
    }))
  }, [jobs])
  const columns = React.useMemo<TheasoftColumnDef<JobLogDto>[]>(
    () => [
      { accessorKey: "name", header: "Job Name" },
      { accessorKey: "jobType", header: "Job Type" },
      {
        accessorKey: "start",
        header: "Start Time",
        cell: ({ row }) => getFormattedDate(row.original.start, "L LTS"),
      },
      {
        accessorKey: "end",
        header: "End Time",
        cell: ({ row }) => getFormattedDate(row.original.end, "L LTS"),
      },
      { accessorKey: "errorCount", header: "Error Count" },
      { accessorKey: "warningCount", header: "Warning Count" },
      { accessorKey: "executionId", header: "Execution ID" },
      { accessorKey: "jobId", header: "Job ID" },
    ],
    []
  )
  const defaultColumnVisibility = React.useMemo<VisibilityState>(
    () => ({ executionId: false, jobId: false }),
    []
  )

  return (
    <Page
      buttonGroupProps={{
        children: (
          <ResponsiveFilterGroup.Button
            visible={filterVisible}
            onChange={() => setFilterVisible(!filterVisible)}
            filters={{
              dateRange: search.start || search.end,
              jobIds: search.jobIds,
            }}
          />
        ),
      }}
    >
      <ResponsiveFilterGroup
        visible={filterVisible}
        setVisible={setFilterVisible}
        filters={{
          dateRange: search.start || search.end,
          jobIds: search.jobIds,
        }}
        onReset={() =>
          updateSearch({ start: undefined, end: undefined, jobIds: undefined })
        }
      >
        <DateTimePicker
          w={300}
          type="range"
          value={[search.start ?? null, search.end ?? null]}
          onChange={(value) => updateSearch({ start: value[0], end: value[1] })}
          allowSingleDateInRange
        />
        <MultiSelect
          w={300}
          clearable
          data={jobFilterOptions}
          value={search.jobIds?.map((id) => id.toString())}
          onChange={(values) => updateSearch({ jobIds: values.map(Number) })}
        />
      </ResponsiveFilterGroup>
      <InfiniteScrollingTable
        id="automatic-background-jobs-table"
        infiniteQueryResult={queryResult}
        columnDefinitions={columns}
        defaultColumnVisibility={defaultColumnVisibility}
        getRowProps={(row) => ({
          onClick: () =>
            row.original.executionId &&
            navigate({
              to: "$executionId",
              params: { executionId: row.original.executionId },
            }),
        })}
      />
    </Page>
  )
}
